/* eslint-disable no-console */
import { Alert, Divider, useToast, VStack } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import { finleyAssert, date as commonDate } from '@finley-technologies/common';
import { DbTable, Util } from '@finley-technologies/finley-types';
import { extractBorrowingBaseMechanicsConfig } from '@finley-technologies/pointer-methods';
import {
  TapeBuilderUI,
  OperationNodes,
} from '@finley-technologies/tape-builder';
import type { TapeBuilderNodeAbstract } from '@finley-technologies/tape-builder';

import { Maybe } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  getCreditFacilityBySlug,
  useGraph,
  OrgSlugToOrgMapType,
} from '../GraphContext';
import { defaultToastOptions } from '../utils';
import { CalcAgentOperationWithBBM } from './CalcAgentOperationWithBBM';
import {
  CreditAgreementSelection,
  CreditAgreementSelectKey,
} from './CreditAgreementSelectKey';
import {
  useGetCreditAgreementFullArtifactWipQuery,
  useGetCurrentCreditAgreementFullArtifactQuery,
} from 'packages/creditAgreements/requests/requests.gql.generated';
import {
  CreditAgreement,
  parseCreditAgreementFullArtifact,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

// Helpers
const getCreditAgreementDetails = (creditAgreement: string) => {
  const parsedCA: Maybe<CreditAgreement.DomainEntity.CreditAgreementFullArtifact> = parseCreditAgreementFullArtifact(
    creditAgreement,
  );
  if (!parsedCA) {
    return null;
  }
  const sourceBorrowingBaseMechanics =
    parsedCA.borrowingBaseMechanics.calcAgentMechanics;
  return {
    borrowingBaseMechanics: extractBorrowingBaseMechanicsConfig(
      sourceBorrowingBaseMechanics,
    ),
    facilityType: parsedCA.facilityType,
  };
};

// Exports
type CalcAgentOperationPanelWithCfIdProps = {
  tapeBuilderNode: OperationNodes.CalcAgent.UINode;
  creditFacilityId: string;
  orgSlugMap: OrgSlugToOrgMapType;
};
export const CalcAgentOperationPanelWithCfId = ({
  tapeBuilderNode,
  creditFacilityId,
  orgSlugMap,
}: CalcAgentOperationPanelWithCfIdProps) => {
  const [facilityType, setFacilityType] = useState<DbTable.FacilityType>(
    'creditFacility',
  );
  const [
    bbm,
    setBBM,
  ] = useState<CreditAgreement.Config.ResolvedCalcAgentMechanics | null>(null);
  const [reportingDate, setReportingDate] = useState<Util.FullDateStr>(
    commonDate.currentDateStr(),
  );
  const [fileVersion, setFileVersion] = useState<number>(1);

  const toast = useToast(defaultToastOptions);

  const {
    loading: isGetCreditAgreementFullArtifactWipLoading,
    data: getCreditAgreementFullArtifactWipData,
  } = useGetCreditAgreementFullArtifactWipQuery({
    variables: {
      creditFacilityId,
    },
    onError: (err) => {
      toast({
        id: `${Date.now()}`,
        status: 'error',
        title: 'Error fetching Credit Agreement WIP',
        description: err.message,
      });
    },
    // fetchPolicy: 'network-only',
  });

  const {
    loading: isGetCurrentCreditAgreementFullArtifactLoading,
    data: getCurrentCreditAgreementFullArtifactData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: {
      creditFacilityId,
    },
    onError: (err) => {
      toast({
        id: `${Date.now()}`,
        status: 'error',
        title: 'Error fetching Credit Agreement',
        description: err.message,
      });
    },
    // fetchPolicy: 'network-only',
  });

  // update BBM on every switch between WIP/latestCommitted
  const onCreditAgreementChanged = ({
    creditAgreementSelection,
    reportingDate: newReportingDate,
    fileVersion: newFileVersion,
  }: {
    creditAgreementSelection: CreditAgreementSelectKey;
    reportingDate: Util.FullDateStr;
    fileVersion: number;
  }) => {
    let creditAgreement: string | null = null;
    if (
      creditAgreementSelection === 'wip' &&
      getCreditAgreementFullArtifactWipData &&
      getCreditAgreementFullArtifactWipData.creditAgreementFullArtifactWip
    ) {
      creditAgreement =
        getCreditAgreementFullArtifactWipData.creditAgreementFullArtifactWip.wip
          .creditAgreement;
    } else if (
      creditAgreementSelection === 'latestCommitted' &&
      getCurrentCreditAgreementFullArtifactData &&
      getCurrentCreditAgreementFullArtifactData.currentCreditAgreementFullArtifact
    ) {
      creditAgreement =
        getCurrentCreditAgreementFullArtifactData
          .currentCreditAgreementFullArtifact.creditAgreement;
    }

    if (creditAgreement) {
      const caDetails = getCreditAgreementDetails(creditAgreement);
      setBBM(caDetails ? caDetails.borrowingBaseMechanics : null);
      if (caDetails) {
        setFacilityType(caDetails.facilityType);
      }
    } else {
      setBBM(null);
    }
    setReportingDate(newReportingDate);
    setFileVersion(newFileVersion);
  };

  // set BBM initially.
  useEffect(() => {
    if (
      getCurrentCreditAgreementFullArtifactData &&
      getCurrentCreditAgreementFullArtifactData.currentCreditAgreementFullArtifact
    ) {
      const caDetails = getCreditAgreementDetails(
        getCurrentCreditAgreementFullArtifactData
          .currentCreditAgreementFullArtifact.creditAgreement,
      );
      setBBM(caDetails ? caDetails.borrowingBaseMechanics : null);
      if (caDetails) {
        setFacilityType(caDetails.facilityType);
      }
    } else {
      setBBM(null);
    }
  }, [getCurrentCreditAgreementFullArtifactData]);

  if (isGetCreditAgreementFullArtifactWipLoading) {
    return <Alert>Fetching WIP...</Alert>;
  }
  if (isGetCurrentCreditAgreementFullArtifactLoading) {
    return <Alert>Fetching Credit Agreement...</Alert>;
  }

  if (
    !getCreditAgreementFullArtifactWipData ||
    !getCurrentCreditAgreementFullArtifactData
  ) {
    return <Alert>Credit Agreement Data not available...</Alert>;
  }

  const hasWip = !!getCreditAgreementFullArtifactWipData.creditAgreementFullArtifactWip;

  return (
    <VStack alignItems="flex-start">
      <CreditAgreementSelection
        hasWip={hasWip}
        onChange={onCreditAgreementChanged}
      />
      <Divider />
      {bbm && (
        <CalcAgentOperationWithBBM
          bbm={bbm}
          facilityType={facilityType}
          creditFacilityId={creditFacilityId}
          tapeBuilderNode={tapeBuilderNode}
          reportingDate={reportingDate}
          fileVersion={fileVersion}
          orgSlugMap={orgSlugMap}
        />
      )}
    </VStack>
  );
};

type CalcAgentOperationPanelProps = {
  tapeBuilderNode: TapeBuilderNodeAbstract;
  orgSlugMap: OrgSlugToOrgMapType;
};
export const CalcAgentOperationPanel = ({
  tapeBuilderNode,
  orgSlugMap,
}: CalcAgentOperationPanelProps) => {
  const graphContext = useGraph();

  finleyAssert(
    TapeBuilderUI.operationNodes.operation_calcAgent.isNode(tapeBuilderNode),
    'node is not a calcAgent node',
  );
  const creditFacilityId = getCreditFacilityBySlug(
    graphContext,
    tapeBuilderNode.configuration.creditFacilitySlug,
  );

  if (!creditFacilityId) {
    return (
      <Alert>
        Credit Facility not found:{' '}
        {tapeBuilderNode.configuration.creditFacilitySlug}{' '}
      </Alert>
    );
  }
  return (
    <CalcAgentOperationPanelWithCfId
      tapeBuilderNode={tapeBuilderNode}
      creditFacilityId={creditFacilityId}
      orgSlugMap={orgSlugMap}
    />
  );
};
